import React, { useState } from 'react';
import styled from "styled-components";
import { 
  ChevronDown, 
  ChevronUp, 
  Search, 
  Info, 
  AlertTriangle, 
  ContactRound, 
  RefreshCw, 
  BadgeCheck 
} from "lucide-react";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #fff;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: #fff;
  margin: 0;
`;

const SectionCard = styled.div`
  background-color: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 6px;
  overflow: hidden;
`;

const SectionHeader = styled.button`
  width: 100%;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  text-align: left;
  cursor: pointer;
  outline: none;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  margin: 0;
`;

const SectionContent = styled.div`
  padding: 0 16px 16px 16px;
  display: flex;
  flex-direction: column;
`;

const SubsectionTitle = styled.h4`
  font-size: 13px;
  font-weight: bold;
  color: #fff;
  margin: 12px 0 0 0;
`;

const Label = styled.label`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 10px;
  display: block;
`;

const Required = styled.span`
  color: #ef4444;
`;

const InputWrapper = styled.div`
  position: relative;
  margin-top: 6px;
`;

const Select = styled.select`
  width: 100%;
  background-color: #1e2024;
  border: 1px solid #3a4142;
  border-radius: 4px;
  padding: 8px 12px;
  color: #fff;
  font-size: 13px;
  appearance: none;
  outline: none;
  cursor: pointer;
  padding-right: 36px;

  &:focus {
    border-color: #3b82f6;
  }
`;

const Input = styled.input`
  width: 100%;
  background-color: #1e2024;
  border: 1px solid #3a4142;
  border-radius: 4px;
  padding: 8px 12px;
  color: #fff;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #3b82f6;
  }
`;

const SearchInput = styled(Input)`
  padding-left: 36px;
`;

const UploadGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const UploadCardBox = styled.div`
  background-color: #1e2024;
  border: 1px dashed #3a4142;
  border-radius: 4px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const UploadIconWrapper = styled.div`
  position: relative;
  width: 48px;
  height: 36px;
  border-radius: 4px;
  background-color: #292d2e;
  display: grid;
  place-items: center;
`;

const UploadButton = styled.button`
  height: 30px;
  padding: 0 12px;
  border-radius: 4px;
  background-color: #292d2e;
  border: 1px solid #3a4142;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #3a4142;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  margin-top: 16px;
  height: 38px;
  border-radius: 4px;
  background-color: #1475e1;
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #1a84f3;
  }

  &:disabled {
    background-color: rgba(20, 117, 225, 0.4);
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
  }
`;

const AlertBox = styled.div`
  display: flex;
  gap: 8px;
  border: 1px solid rgba(245, 158, 11, 0.3);
  background-color: rgba(245, 158, 11, 0.06);
  border-radius: 4px;
  padding: 10px 12px;
  margin-top: 10px;
`;

const AlertText = styled.div`
  color: #f59e0b;
  font-size: 12px;
  line-height: 1.4;
`;

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <SectionCard>
      <SectionHeader onClick={() => setOpen(!open)}>
        <SectionTitle>{title}</SectionTitle>
        {open ? <ChevronUp size={16} style={{ color: '#fff' }} /> : <ChevronDown size={16} style={{ color: '#fff' }} />}
      </SectionHeader>
      {open && <SectionContent>{children}</SectionContent>}
    </SectionCard>
  );
}

function UploadCard({ label, icon }) {
  return (
    <UploadCardBox>
      <UploadIconWrapper>
        <ContactRound className="w-8 h-8 text-muted-foreground" />
        {icon === "refresh" ? (
          <RefreshCw className="w-4 h-4 absolute -right-1 -top-1" style={{ color: "#1fc16b" }} />
        ) : (
          <span className="absolute -right-1 -top-1" style={{ color: "#1fc16b", fontSize: '14px', fontWeight: 'bold' }}>→</span>
        )}
      </UploadIconWrapper>
      <p style={{ color: '#9ca3af', fontSize: '11px', margin: 0 }}>Accepted format: .png, .jpg, .pdf</p>
      <UploadButton type="button">{label}</UploadButton>
    </UploadCardBox>
  );
}

const Verification = () => {
  const [country, setCountry] = useState("Germany");
  const [docType, setDocType] = useState("Driver license");

  return (
    <PageContainer>

      <Section title="Identity Verification" defaultOpen={true}>
        <SubsectionTitle>Residence Details</SubsectionTitle>
        
        <Label>Country <Required>*</Required></Label>
        <InputWrapper>
          <Select value={country} onChange={(e) => setCountry(e.target.value)}>
            <option>Germany</option>
            <option>United Kingdom</option>
            <option>United States</option>
            <option>India</option>
            <option>France</option>
          </Select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: '#9ca3af' }} />
        </InputWrapper>

        <Label>Residential Address <Required>*</Required></Label>
        <InputWrapper>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: '#9ca3af' }} />
          <SearchInput placeholder="Search your address" />
        </InputWrapper>

        <Label>City <Required>*</Required></Label>
        <InputWrapper>
          <Input />
        </InputWrapper>

        <Label>Postal Code <Required>*</Required></Label>
        <InputWrapper>
          <Input />
        </InputWrapper>

        <SubsectionTitle style={{ marginTop: '28px' }}>Upload Identification</SubsectionTitle>
        <p style={{ color: '#9ca3af', fontSize: '13px', margin: '4px 0 0 0' }}>Please upload a clear photo of your ID to continue.</p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px' }}>
          <Label style={{ margin: 0 }}>Document Type <Required>*</Required></Label>
          <Info size={16} color="#9ca3af" />
        </div>

        <InputWrapper>
          <Select value={docType} onChange={(e) => setDocType(e.target.value)}>
            <option>Driver license</option>
            <option>National ID card</option>
            <option>International passport</option>
            <option>Other</option>
          </Select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: '#9ca3af' }} />
        </InputWrapper>

        <UploadGrid>
          <div>
            <Label style={{ marginTop: 0, marginBottom: '8px' }}>Front Side <Required>*</Required></Label>
            <UploadCard label="Upload Front Side" icon="id" />
          </div>
          <div>
            <Label style={{ marginTop: 0, marginBottom: '8px' }}>Back Side <Required>*</Required></Label>
            <UploadCard label="Upload Back Side" icon="refresh" />
          </div>
        </UploadGrid>

        <SubmitButton type="button">Submit for Verification</SubmitButton>
      </Section>

      <Section title="Proof of Address">
        <SubsectionTitle>Upload Proof of Address</SubsectionTitle>
        <p style={{ color: '#9ca3af', fontSize: '13px', margin: '4px 0 0 0' }}>Please upload your proof of address.</p>

        <AlertBox>
          <AlertTriangle size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
          <AlertText>Please complete identity verification first.</AlertText>
        </AlertBox>

        <Label>Proof of Address <Required>*</Required></Label>
        <div style={{ marginTop: '8px', opacity: 0.5, pointerEvents: 'none' }}>
          <UploadCard label="Upload Proof of Address" icon="id" />
        </div>

        <SubmitButton disabled type="button">Submit for Verification</SubmitButton>
      </Section>

      <Section title="Source of Funds">
        <SubsectionTitle>Occupation</SubsectionTitle>
        
        <AlertBox>
          <AlertTriangle size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
          <AlertText>Please complete proof of address first.</AlertText>
        </AlertBox>

        {["Occupation Industry", "Occupation", "Occupation Experience"].map((l) => (
          <div key={l}>
            <Label>{l} <Required>*</Required></Label>
            <InputWrapper>
              <Select defaultValue="" disabled style={{ color: '#9ca3af' }}>
                <option value="" disabled>Select your {l.toLowerCase()}</option>
              </Select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: '#9ca3af' }} />
            </InputWrapper>
          </div>
        ))}

        <SubsectionTitle style={{ marginTop: '28px' }}>Upload Source of Funds</SubsectionTitle>
        <p style={{ color: '#9ca3af', fontSize: '13px', margin: '4px 0 0 0' }}>Please upload a clear, readable document with all 4 corners visible.</p>

        <Label>Source of Funds <Required>*</Required></Label>
        <div style={{ marginTop: '8px', opacity: 0.5, pointerEvents: 'none' }}>
          <UploadCard label="Upload Source of Funds" icon="id" />
        </div>

        <SubmitButton disabled type="button">Submit for Verification</SubmitButton>
      </Section>
    </PageContainer>
  );
};

export default Verification;
